import { ModalWithForm } from '@/components/ats';
import { useRejectEmailFormConfig } from '@/formConfigs/useRejectEmailFormConfig';
import { CreateProjectDrawer } from '@/components/shared/project-modal/create-project-drawer';
import { useRef } from 'react';
import { useGetDefaultFrom } from '@/hooks';

export const RejectModal = ({
  rejectModalActions,
  handleSubmit = () => {},
  isLoading = false,
  actionApplication,
  initialValues = null,
  maxHeight = '70vh',
  isProjectCreationAllowed = true,
}) => {
  const { isOpen, onClose } = rejectModalActions;
  // The Formik instance, not the dialog node. It used to be handed to
  // `modalRef` (a DOM ref on ModalContent) and never to the config hook, so
  // `setCreatedProjectData`'s `formRef?.current?.setValues` was a silent no-op
  // and a freshly created project was never selected.
  const formRef = useRef(null);
  const {
    rejectFormConfig,
    isCreateProjectOpen,
    handleModalClose,
    setCreatedProjectData,
  } = useRejectEmailFormConfig({
    formRef,
    isProjectCreationAllowed,
    isQuickAppReview: true,
    atsJobStageId: actionApplication?.job_stage_id,
  });

  const values = initialValues?.sendRejectEmail
    ? {
        reject_without_email: true,
        ats_rejection_reason_id: initialValues?.rejectionReason?.id,
        sendRejectEmail: initialValues?.sendRejectEmail,
        schedule_at: {
          formattedTime: initialValues?.scheduleAt,
          timezoneValue: initialValues?.timeZone,
        },
        ...initialValues,
      }
    : {
        reject_without_email: true,
        ats_rejection_reason_id: initialValues?.rejectionReason?.id,
      };

  const { from: defaultFrom, fromUserObject: defaultFromUserObject } =
    useGetDefaultFrom({});

  // Seed the org default only when nothing is stored. This used to overwrite
  // unconditionally, and `useGetDefaultFrom` always resolves to something (it
  // falls back to the signed-in user), so reopening a saved rejection always
  // showed the default instead of the sender it was scheduled with (ENG-3828).
  if (defaultFrom && !initialValues?.from) {
    initialValues = {
      ...initialValues,
      from: defaultFrom,
      ...(defaultFromUserObject && { fromUserObject: defaultFromUserObject }),
    };
  }

  return (
    <>
      <ModalWithForm
        formConfig={rejectFormConfig({
          contact: actionApplication?.contact,
          initialValues,
        })}
        handleSubmit={handleSubmit}
        headerText="Reject candidate?"
        subHeaderText="Select a reason for rejecting this candidate"
        initialValues={{
          ...values,
        }}
        isCtaLoading={isLoading}
        isOpen={isOpen}
        enableBorder={true}
        customFormRef={formRef}
        onClose={onClose}
        // Hidden, never closed: `isOpen={false}` would unmount FormBuilder and
        // throw away the rejection reason, the composed email and the survey
        // the recruiter had already filled in. Staying mounted also keeps the
        // email composer from re-running its seed on the way back.
        //
        // Modals sit at 1440 and drawers at 1400 (src/constants/zIndex.ts), so
        // a drawer opened from inside a modal renders behind it by design.
        // Standing the dialog down for the duration sidesteps that ladder
        // rather than punching a hole in it (ENG-3828).
        trapFocus={!isCreateProjectOpen}
        primaryCtaText="Reject"
        modalBodyProps={{ maxHeight: maxHeight, overflowY: 'auto' }}
        modalHeaderProps={{ p: '12px 24px' }}
        // `ModalWithForm` hardcodes a 500px dialog and ignores `modalSize`, so
        // the width has to come through `modalContentProps` (spread last). At
        // 500px the editor toolbar wrapped and pushed the survey control onto a
        // second line (ENG-3828). 672px is Chakra's `2xl`, which is what the
        // list-page reject modal already renders at.
        modalContentProps={{
          w: { base: '90%', sm: '90%', md: '672px' },
          maxW: { base: '90%', sm: '90%', md: '672px' },
          minW: { base: '90%', sm: '90%', md: '672px' },
          // `display: none` rather than opacity/visibility — the dialog must
          // stop taking hit-tests too, or its invisible surface still swallows
          // clicks meant for the drawer underneath.
          //
          // `containerProps` matters as much as the content: Chakra wraps
          // ModalContent in `.chakra-modal__content-container`, which is
          // `position: fixed; width: 100vw; height: 100vh` with no
          // `pointer-events: none`, and our theme puts it at 1440. Hiding only
          // the content and the overlay left that full-viewport layer sitting
          // over the drawer, swallowing every click.
          ...(isCreateProjectOpen && {
            display: 'none',
            containerProps: { display: 'none' },
          }),
        }}
        modalOverlayProps={
          isCreateProjectOpen ? { display: 'none' } : undefined
        }
      />
      <CreateProjectDrawer
        modalActions={{
          isOpen: isCreateProjectOpen,
          onClose: handleModalClose,
        }}
        setCreatedProjectData={setCreatedProjectData}
        mode="create"
      />
    </>
  );
};
